import {
  DatabaseFieldType,
  type DatabaseField,
  type DatabaseValue
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  type DatabaseChange,
  type ValueChange
} from '@databases/utils/database/changesets';
import { DATE_VALUE_TODAY } from '@databases/utils/database/field-types/date';
import { type Dispatch, type SetStateAction } from 'react';
import { type IntlShape } from 'react-intl-next';

import { messages } from '../messages.ts';
import {
  type DefaultValueMode,
  type DefaultValueModeSelectConfig,
  getDefaultValueModeSelectOptions
} from '../shared/DefaultValueModeSelect.tsx';

const getDateDefaultValueMode = (
  field: DatabaseField,
  value: DatabaseValue,
  optimisticMode: DefaultValueMode | null
): DefaultValueMode | null => {
  if (optimisticMode) {
    return optimisticMode;
  }

  if (field.type !== DatabaseFieldType.DATE || !field.settings.defaultEnabled) {
    return null;
  }

  return value.value === DATE_VALUE_TODAY ? 'current' : 'specific';
};

type DateDefaultValueModeSelectConfigArgs = {
  addChangeToDraft: (change: DatabaseChange) => void;
  effectiveDefaultValue: DatabaseValue;
  field: DatabaseField<DatabaseFieldType.DATE>;
  formatMessage: IntlShape['formatMessage'];
  optimisticDefaultValueMode: DefaultValueMode | null;
  setOptimisticDefaultValue: Dispatch<SetStateAction<DatabaseValue | null>>;
  setOptimisticDefaultValueMode: Dispatch<SetStateAction<DefaultValueMode | null>>;
};

export const getDateDefaultValueModeSelectConfig = ({
  addChangeToDraft,
  effectiveDefaultValue,
  field,
  formatMessage,
  optimisticDefaultValueMode,
  setOptimisticDefaultValue,
  setOptimisticDefaultValueMode
}: DateDefaultValueModeSelectConfigArgs): DefaultValueModeSelectConfig => {
  const options = getDefaultValueModeSelectOptions({
    current: formatMessage(messages.defaultValueModeCurrentDate),
    noDefault: formatMessage(messages.defaultValueModeNoDefault),
    specific: formatMessage(messages.defaultValueModeSpecificDate)
  });

  return {
    onChange: (mode) => {
      setOptimisticDefaultValueMode(mode);

      switch (mode) {
        case 'no-default':
          setOptimisticDefaultValue({
            type: DatabaseFieldType.DATE,
            value: undefined
          } as DatabaseValue<DatabaseFieldType.DATE>);
          addChangeToDraft({
            type: DatabaseChangeType.SET_DEFAULT_ENABLED,
            fieldId: field.id,
            defaultEnabled: false
          });
          return;

        case 'specific':
          if (
            !field.settings.defaultEnabled ||
            effectiveDefaultValue.value === DATE_VALUE_TODAY
          ) {
            setOptimisticDefaultValue({
              type: DatabaseFieldType.DATE,
              value: undefined
            } as DatabaseValue<DatabaseFieldType.DATE>);
          }
          return;

        case 'current': {
          const currentDateDefaultValue = {
            type: DatabaseFieldType.DATE,
            value: DATE_VALUE_TODAY
          } as DatabaseValue<DatabaseFieldType.DATE>;
          const change = {
            type: DatabaseFieldType.DATE,
            value: DATE_VALUE_TODAY
          } as ValueChange;

          setOptimisticDefaultValue(currentDateDefaultValue);
          if (!field.settings.defaultEnabled) {
            addChangeToDraft({
              type: DatabaseChangeType.SET_DEFAULT_ENABLED,
              fieldId: field.id,
              defaultEnabled: true
            });
          }
          addChangeToDraft({
            type: DatabaseChangeType.BULK_SET_UNDEFINED_VALUES,
            fieldId: field.id,
            value: currentDateDefaultValue,
            change
          });
          return;
        }
      }
    },
    options,
    placeholder: formatMessage(messages.defaultValueModeDatePlaceholder),
    value: getDateDefaultValueMode(
      field,
      effectiveDefaultValue,
      optimisticDefaultValueMode
    )
  };
};
